import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

// Single source of truth for the backend URL (api.js uses the same var).
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

export function useAuth() {
  return useContext(AuthContext);
}

async function authRequest(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.detail || 'Authentication failed');
  }
  // Backend returns { access_token, token_type, user_id, email, name, role }
  return {
    id: data.user_id,
    email: data.email,
    name: data.name,
    role: data.role || 'user',
    token: data.access_token,
  };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    const user = await authRequest('/api/auth/login', { email, password });
    setCurrentUser(user);
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }

  async function register(email, password, name) {
    const user = await authRequest('/api/auth/register', { email, password, name });
    setCurrentUser(user);
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }

  function logout() {
    setCurrentUser(null);
    localStorage.removeItem('user');
    return Promise.resolve();
  }

  // Restore session on load
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.token) {
        setCurrentUser(user);
      }
    } catch (_) {
      /* ignore malformed storage */
    }
    setLoading(false);
  }, []);

  const value = { currentUser, login, register, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
